import { toast } from '@teamsparta/stack-toast';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import type { CommunityPostCommentSortBy } from '@/api/community/comment/comment.dto';
import { DEFAULT_SORT_BY } from '@/hooks/react-query/community/comment';
import { usePostCommunityPostDetailLike } from '@/hooks/react-query/community/like';
import { useDeleteCommunityPost } from '@/hooks/react-query/community/post';
import { ApiException } from '@/lib/exceptions';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { isMobileDevice } from '@/lib/utils/device';

export function useEditPost() {
  const router = useRouter();
  const postId = router.query.id as string;

  const handleEdit = useCallback(() => {
    router.push(`/community/post/edit/${postId}`);
  }, [postId, router]);

  return { handleEdit };
}

export function useDeletePost() {
  const router = useRouter();
  const postId = router.query.id as string;
  const { mutate: deletePost, isPending } = useDeleteCommunityPost();

  const handleDelete = useCallback(() => {
    deletePost(
      { postId },
      {
        onSuccess: () => {
          router.push('/community');
          toast('글이 삭제되었습니다.');
        },
        onError: (error) => {
          if (error instanceof ApiException) {
            toast.error(error.message, { duration: 3000 });
          }
        },
      },
    );
  }, [deletePost, postId, router]);

  return { handleDelete, isPending };
}

export function useLike(postId: string) {
  const { mutate: like } = usePostCommunityPostDetailLike({ postId });

  const handleLike = useCallback(() => {
    like({ postId });
  }, [like, postId]);

  return { handleLike };
}

export function useSortBy(postId: string) {
  const [sortBy, setSortBy] =
    useState<CommunityPostCommentSortBy>(DEFAULT_SORT_BY);

  const handleChangeSortBy = useCallback(
    (sortBy: CommunityPostCommentSortBy) => {
      setSortBy(sortBy);
    },
    [postId],
  );

  return { sortBy, handleChangeSortBy };
}

export function useShare(postId: string) {
  const handleShare = useCallback(() => {
    const title = document.title;
    const url = window.location.href;

    if (isMobileDevice()) {
      navigator.share({
        title,
        url,
      });
    } else {
      copyToClipboard(url, {
        onSuccess: () => {
          toast.success('링크가 복사되었습니다.');
        },
      });
    }
  }, [postId]);

  return { handleShare };
}
